import { featureSaveProficiencies } from '../../../shared/featureSaveProficiencies';

const ABILITY_ALIASES = Object.freeze({
  str: 'strength', dex: 'dexterity', con: 'constitution', int: 'intelligence', wis: 'wisdom', cha: 'charisma',
});

export const normalizeSaveAbility = (ability) => {
  const value = String(ability || '').trim().toLowerCase();
  return ABILITY_ALIASES[value] || (Object.values(ABILITY_ALIASES).includes(value) ? value : null);
};

const scoreModifier = (score) => Math.floor((Number(score) - 10) / 2);
const finite = (...values) => values.map(Number).find(Number.isFinite);

/** Describe any combatant's save without knowing which feature requested it. */
export const getCombatantSaveModifier = (combatant, ability) => {
  const full = normalizeSaveAbility(ability);
  if (!full) throw new Error('A valid saving throw ability is required.');
  const short = Object.keys(ABILITY_ALIASES).find((key) => ABILITY_ALIASES[key] === full);
  const explicit = finite(
    combatant?.savingThrows?.[full]?.bonus, combatant?.savingThrows?.[short]?.bonus,
    combatant?.savingThrows?.[full], combatant?.savingThrows?.[short],
    combatant?.saves?.[full], combatant?.saves?.[short], combatant?.[`${short}Save`],
  );
  if (explicit !== undefined) return explicit;
  const abilityModifier = finite(
    combatant?.abilityModifiers?.[full], combatant?.abilityModifiers?.[short],
    combatant?.abilities?.[full]?.modifier, combatant?.abilities?.[short]?.modifier,
  );
  const base = abilityModifier ?? scoreModifier(finite(
    combatant?.abilities?.[full]?.score, combatant?.abilities?.[short]?.score,
    combatant?.[full], combatant?.[short], 10,
  ));
  const proficient = Boolean(combatant?.savingThrowProficiencies?.includes?.(full) ||
    combatant?.savingThrowProficiencies?.includes?.(short) || combatant?.savingThrows?.[full]?.proficient ||
    featureSaveProficiencies(combatant).includes(short));
  const proficiency = proficient ? (finite(combatant?.proficiencyBonus, combatant?.profBonus, 0) || 0) : 0;
  const other = finite(combatant?.saveModifiers?.[full], combatant?.saveModifiers?.[short], 0) || 0;
  return base + proficiency + other;
};

export const createTargetedSaveRequest = ({ requestId, replayIdentity = requestId, sourceCombatantId, targetCombatantId, ability, dc, targetSaveModifier, mode = 'normal' }) => {
  const normalizedAbility = normalizeSaveAbility(ability);
  if (![requestId, replayIdentity, sourceCombatantId, targetCombatantId].every((v) => typeof v === 'string' && v.trim()) ||
      !normalizedAbility || !Number.isFinite(dc) || !Number.isFinite(targetSaveModifier) || !['normal', 'advantage', 'disadvantage'].includes(mode)) {
    throw new Error('Invalid targeted saving throw request.');
  }
  return { requestId, replayIdentity, sourceCombatantId, targetCombatantId, ability: normalizedAbility, dc, targetSaveModifier, mode, status: 'pending' };
};

export const resolveTargetedSave = (request, d20Result) => {
  if (request?.status !== 'pending' || !Number.isInteger(d20Result) || d20Result < 1 || d20Result > 20) throw new Error('Invalid saving throw result.');
  const total = d20Result + request.targetSaveModifier;
  return { ...request, resolutionId: request.requestId, d20Result, total, success: total >= request.dc, status: 'resolved' };
};
